import axios from "axios";
import type { BatteryPayload, KioskInfo } from "@/utils/kioskBattery";

/**
 * 端末 heartbeat の収集・送信。
 *
 * 実機テレメトリ（画面/向き/UA 等）と端末設定の初回 backfill を adbox-admin-rails の
 * `POST /api/devices/:gateway_id/heartbeat` へ送る。 認証は表示設定 API と同じ共有 Bearer。
 *
 * 設計: adbox-admin-rails docs/design/features/device_telemetry.md
 */

const BASE_URL = process.env.VUE_APP_ADBOX_DEVICE_API_URL;
const TOKEN = process.env.VUE_APP_ADBOX_DEVICE_API_TOKEN;

const http = axios.create({
  baseURL: BASE_URL,
  timeout: 6000,
  headers: TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {},
});

export interface DeviceMetrics {
  deviceUnitUid: string;
  screen: { width: number; height: number };
  orientation: string;
  devicePixelRatio: number;
  viewport: { width: number; height: number };
  userAgent: string;
}

export interface HeartbeatPayload extends DeviceMetrics {
  deviceLocalSettings?: Record<string, unknown>; // 初回 backfill 用（任意）
  battery?: BatteryPayload; // 電池内蔵端末 (LUCA) のみ (KioskBridge 由来・任意)
  kiosk?: KioskInfo; // キオスク状態 (適用configVersion/APK版。DPC のみ・任意)
  appBuildId?: string; // 配信 Web の buildId (release_rollout.md §9)
  appBuildStale?: boolean; // 自己リロードで新版に到達できず諦めた状態か
  clientKind?: ClientKind; // 端末種別の自己申告 (§12.6)。実機とヒト閲覧の切り分け材料
}

/**
 * 端末種別の自己申告 (§12.6)。kiosk / twa は実機確定。browser は PC サイネージと
 * ヒト閲覧の両方があり得るため、これ単独では実機と断定しない (サーバ側は継続稼働と併用)。
 */
export type ClientKind = "kiosk" | "twa" | "browser";

export const deriveClientKind = (
  hasKioskBridge: boolean,
  hostname: string
): ClientKind => {
  if (hasKioskBridge) return "kiosk"; // DPC の addJavascriptInterface = 実機確定
  if (hostname.startsWith("twa.")) return "twa"; // TWA 専用ホスト
  return "browser";
};

// ブラウザから実測情報を収集する。
export const collectDeviceMetrics = (deviceUnitUid: string): DeviceMetrics => ({
  deviceUnitUid,
  screen: { width: window.screen.width, height: window.screen.height },
  orientation: window.screen.orientation?.type ?? "",
  devicePixelRatio: window.devicePixelRatio,
  viewport: { width: window.innerWidth, height: window.innerHeight },
  userAgent: navigator.userAgent,
});

/**
 * heartbeat を POST する。 成功なら true。 env 未設定 / 端末未登録 / エラーは false
 * （端末の表示動作には一切影響しない）。
 */
export const sendHeartbeat = async (
  gatewayId: string,
  payload: HeartbeatPayload
): Promise<boolean> => {
  if (!BASE_URL || !TOKEN || !gatewayId) return false;
  try {
    await http.post(
      `/api/devices/${encodeURIComponent(gatewayId)}/heartbeat`,
      payload
    );
    return true;
  } catch (e) {
    console.warn("[heartbeat] 送信失敗:", e);
    return false;
  }
};
